import { useLocation, useNavigate } from "react-router-dom";
import { PendingLicencePurchase } from "../../../types/PendingLicencePurchase/PendingLicencePurchase";
import { useEffect, useState } from "react";
import usePendingLicencePurchaseService from "../../../hooks/usePendingLicencePurchaseService";
import styled from "styled-components";
import { useLayerService } from "../../../hooks";
import { Layer } from "../../../types/Layer";
import { NavDropdown } from "react-bootstrap";
import LicenceCard from "./LicenceCard";
import { useCurrencyContext } from "../../../context/Currency/CurrencyContext";
import CurrencyModal from "../../modal/Currency/CurrencyModal";
import { Currency } from "../../../types/Currency/Currency";
import { LicenceTypePurchase } from "../../../types/Licences/LicenceTypePurchase";
import LicencePurchaseConfirmation from "../../modal/LicencePurchaseConfirmation/LicencePurchaseConfirmation";
import PurchaseLicenceProvider from "../../../context/PurchaseLicence/PurchaseLicenceProvider";
import useSsoService from "../../../hooks/useSsoService";
import { PurchaseLicenceRequest } from "../../../types/PendingLicencePurchase/PurchaseLicenceRequest";
import { PaymentType } from "../../../constant/PaymentType";
import { LicenceType } from "../../../types/Licences/LicenceType";

const Container = styled.div`
  background-color: rgb(227, 242, 253);
  height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 0.5rem;
  align-items: flex-start;
`;

const PurchaseHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
`;

const Content = styled.div`
  height: calc(100vh - 8px);
  padding: 1rem 1rem 1rem 1rem;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 5px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
`;

const LicenceRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  overflow-x: auto;
  margin-top: 1rem;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  height: 100%;
  justify-content: center;
  gap: 1rem;
`;
interface ConfirmationModalData {
  licence: LicenceTypePurchase;
  userCount?: number;
}

const PurchaseLicence = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ssoService = useSsoService();
  const pendingLicencePurchaseService = usePendingLicencePurchaseService();
  const layerService = useLayerService();

  const { selectedCurrency, setSelectedCurrency } = useCurrencyContext();
  const [loading, setLoading] = useState(false);
  const [pendingLicencePurchase, setPendingLicencePurchase] =
    useState<PendingLicencePurchase>();
  const [allLayers, setAllLayers] = useState<Layer[]>([]);
  const [selectedLicenceTypeOption, setSelectedLicenceTypeOption] = useState<{
    label: string;
    value: string;
  }>({ label: "Single User", value: "SingleUser" });
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [purchaseModalData, setPurchaseModalData] =
    useState<ConfirmationModalData>();

  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get("id");
  const queryHash = searchParams.get("hash");

  const stateId = location.state?.id;
  const stateHash = location.state?.hash;

  const id = stateId || queryId;
  const hash = stateHash || queryHash;
  const isTraccPurchase =
    (searchParams.get("traccPurchase") || "").toLowerCase() === "true";

  useEffect(() => {
    layerService
      .GetAllLayers()
      .then((res) => {
        setAllLayers(res);
      })
      .catch(() => {
        navigate("/login");
      });
  }, [layerService]);

  useEffect(() => {
    if (id && hash) {
      getPendingLicencePurchase(id, hash);
    }
  }, [id, hash]);

  const getPendingLicencePurchase = (id: string, hash: string) => {
    if (hash && id) {
      setLoading(true);
      pendingLicencePurchaseService
        .getPendingLicencePurchase(id, hash)
        .then((res) => {
          if (!res) {
            navigate("/login");
          } else {
            setPendingLicencePurchase(res);
            setSelectedCurrency(res.transactionCurrency);
          }
        })
        .catch(() => {
          navigate("/login");
        })
        .finally(() => setLoading(false));
    } else {
      navigate("/login");
    }
  };

  const updateCurrency = (currency: Currency) => {
    if (currency) {
      setLoading(true);
      pendingLicencePurchaseService
        .updateCurrency(id, hash, currency.code)
        .then(() => {
          getPendingLicencePurchase(id, hash);
        });
    }
  };

  const onPurchaseLicence = (
    licenceId: string,
    licenceType?: string,
    numberOfUsers?: number
  ) => {
    if (licenceType === "SingleUser" || licenceType === "Concurrent") {
      var request = {
        softwareLicenceId: licenceId,
        pendingLicencePurchaseId: pendingLicencePurchase!.id,
        hash: hash,
        numberOfUsers: numberOfUsers,
      } as PurchaseLicenceRequest;

      ssoService.PurchaseLicence(request).then((r) => {
        window.location.href = r;
      });
    } else if (licenceType === "Trial") {
      ssoService
        .AssignTrialLicence(pendingLicencePurchase!.id, hash, licenceId)
        .then((r) => {
          navigate(
            "/login?" +
              new URLSearchParams({
                token: r.token,
                refreshToken: r.refreshToken,
              }).toString()
          );
        });
    }
  };

  return (
    <PurchaseLicenceProvider
      loading={loading}
      onPurchaseLicence={onPurchaseLicence}
    >
      <Container className="card-theme">
        <Content className="d-flex flex-row w-100">
          <Card className="w-100 text-black bg-white">
            <HeaderRow>
              <PurchaseHeader>
                <h4 className="mt-2">Purchase a licence</h4>
              </PurchaseHeader>
              <div className="d-flex flex-row justify-content-end h-100 gap-4">
                <div className="h-100 d-flex flex-column justify-content-center">
                  <NavDropdown
                    title={
                      <span className="text-black">
                        ({selectedCurrency?.symbol}) {selectedCurrency?.code}
                      </span>
                    }
                  >
                    <NavDropdown.Item
                      href="#"
                      onClick={() => {
                        setShowCurrencyModal(true);
                      }}
                    >
                      Change Currency
                    </NavDropdown.Item>
                  </NavDropdown>
                </div>
              </div>
            </HeaderRow>
            <LicenceRow>
              {pendingLicencePurchase?.licences
                .filter(
                  (l) =>
                    (!isTraccPurchase && l.licenceType === "SingleUser") ||
                    (isTraccPurchase &&
                      (l.licenceType === "SingleUser" ||
                        l.licenceType === "Trial"))
                )
                .filter(
                  (l) =>
                    (!isTraccPurchase &&
                      l.paymentType === PaymentType.RecurringPayment) ||
                    (isTraccPurchase &&
                      (l.paymentType === PaymentType.RecurringPayment ||
                        l.paymentType === PaymentType.Free))
                )
                .sort(
                  (a, b) =>
                    (a.pricingRules?.[0]?.transactionCostIncTax ?? 0) -
                    (b.pricingRules?.[0]?.transactionCostIncTax ?? 0)
                )
                .map((licence) => (
                  <LicenceCard
                    key={licence.softwareLicenceId}
                    licence={licence}
                    allLayers={allLayers}
                    transactionCurrency={
                      pendingLicencePurchase.transactionCurrency
                    }
                  />
                ))}
            </LicenceRow>
          </Card>
        </Content>
      </Container>
      {showCurrencyModal && (
        <CurrencyModal
          onSelect={(currency: Currency) => updateCurrency(currency)}
          onHide={() => setShowCurrencyModal(false)}
        />
      )}
      {purchaseModalData && (
        <LicencePurchaseConfirmation
          licence={purchaseModalData.licence}
          userCount={purchaseModalData.userCount || 0}
          onHide={() => setPurchaseModalData(undefined)}
        />
      )}
    </PurchaseLicenceProvider>
  );
};

export default PurchaseLicence;
